import { FunctionComponent, useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext } from '../../../context';
import { useEventCreateAsCopyMutation } from '../../../services';
import {
  Button,
  ButtonType,
  CookieActionType,
  cookieOptions,
  COPY_EVENT_COOKIE_KEY,
  getOrganisationDomain,
  isAdminLayout,
} from '../../../shared';

interface PasteEventButtonProps {
  onPasted: () => void;
}

export const PasteEventButton: FunctionComponent<PasteEventButtonProps> = ({ onPasted }) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'events' });

  const [cookies, , removeCookie] = useCookies([COPY_EVENT_COOKIE_KEY]);

  const {
    organisation = '',
    sourceChannelId = 0,
    sourceEventId = 0,
  } = { ...cookies[COPY_EVENT_COOKIE_KEY] };

  const hasCopiedEvent = useMemo(
    () => Boolean(sourceEventId && organisation === getOrganisationDomain()),
    [organisation, sourceEventId]
  );

  const { id: channelId } = useContext(ChannelContext).channel;

  const [eventCreateAsCopy] = useEventCreateAsCopyMutation();

  const handlePasteEvent = useCallback(async () => {
    try {
      const { payload } = await toast.promise(
        eventCreateAsCopy({ channelId, sourceChannelId, sourceEventId }).unwrap(),
        {
          pending: ctxT('pasting'),
          success: ctxT('pasted'),
          error: ctxT('paste-error'),
        }
      );

      if (!payload) {
        return toast.error(t('common.error-message'));
      }

      removeCookie(COPY_EVENT_COOKIE_KEY, cookieOptions({ action: CookieActionType.REMOVE }));
      onPasted();
    } catch (_) {}
  }, [
    channelId,
    ctxT,
    eventCreateAsCopy,
    onPasted,
    removeCookie,
    sourceChannelId,
    sourceEventId,
    t,
  ]);

  if (!hasCopiedEvent || !isAdminLayout()) {
    return null;
  }

  return (
    <Button type={ButtonType.secondary} label={ctxT('paste-event')} onClick={handlePasteEvent} />
  );
};
